import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LifeBuoy } from "lucide-react";
import Logo from "@/components/brand/Logo";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/wat-we-doen", label: "Wat we doen" },
  { to: "/projecten", label: "Projecten" },
  { to: "/over-fin", label: "Over FIN" },
  { to: "/voor-gemeenten", label: "Voor gemeenten & partners" },
  { to: "/steun", label: "Steun FIN" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const linkBase = "text-[13px] font-medium tracking-wide transition-colors";
  const linkInactive = "text-cream/80 hover:text-gold";
  const linkActive = "text-gold";

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-petrol/95 backdrop-blur-xl border-b border-cream/10 shadow-soft"
          : "bg-petrol/90 backdrop-blur-lg border-b border-cream/5"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between py-3 md:py-4">
        <Link
          to="/"
          className="flex items-center group shrink-0"
          data-testid="logo-home-link"
          aria-label="Stichting FIN — home"
        >
          <span className="inline-flex items-center gap-2.5">
            <Logo height={56} className="md:!h-[62px] bg-cream/95 rounded-xl p-1.5 shadow-soft transition-transform duration-500 group-hover:scale-[1.03]" />
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-cream text-base md:text-lg tracking-tight">Stichting FIN</span>
              <span className="text-[10px] uppercase tracking-[0.24em] text-gold/90">Future Is Now</span>
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7" aria-label="Hoofdnavigatie">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
              data-testid={`nav-link-${item.to.replace(/\//g, "-") || "home"}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/hulp-nodig"
            className="hidden md:inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition-all rounded-full px-5 py-2.5 text-[13px] font-semibold shadow-gold hover:shadow-lg active:scale-95"
            data-testid="nav-cta-hulp"
          >
            <LifeBuoy size={15} /> Ik zoek hulp
          </Link>
          <button
            type="button"
            className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-cream/20 text-cream hover:bg-cream/10 transition"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Menu sluiten" : "Menu openen"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-cream/10 bg-petrol/98 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-[15px] transition ${
                    isActive ? "bg-cream/10 text-gold font-semibold" : "text-cream/80 hover:bg-cream/5"
                  }`
                }
                data-testid={`mobile-nav-${item.to.replace(/\//g, "-") || "home"}`}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/hulp-nodig"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-gold text-petrol rounded-full px-5 py-3 text-sm font-semibold shadow-gold"
              data-testid="mobile-cta-hulp"
            >
              <LifeBuoy size={15} /> Ik zoek hulp
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
